import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, combineLatest, from, of, timer } from 'rxjs';
import {
  take,
  switchMap,
  filter,
  catchError,
  concatMap,
  map,
  timeout,
  reduce,
} from 'rxjs/operators';

import { ProductV2, ProductV2Variation } from '../../core/models/product-v2.model';
import { OrderDetailsResponse } from '../../core/models/order.model';
import { AuthService } from '../../core/services/auth.service';
import { Ga4Item, GtmService } from '../../core/services/gtm.service';
import { I18nService } from '../../core/services/i18n.service';
import { OrderService } from '../../core/services/order.service';
import { ProductV2Service } from '../../core/services/product-v2.service';
import { SellerCodeStorageService } from '../../core/services/seller-code-storage.service';
import { AvailableOffersFacade } from '../../core/store/available-offers/available-offers.facade';
import { CartFacade } from '../../core/store/cart/cart.facade';
import { retryLinesFromOrderData } from '../../core/utils/order-retry-cart.util';

import { CartService } from './cart.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class UrlCartService {
  private productV2Service = inject(ProductV2Service);
  private cartService = inject(CartService);
  private cartFacade = inject(CartFacade);
  private availableOffersFacade = inject(AvailableOffersFacade);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private i18nService = inject(I18nService);
  private orderService = inject(OrderService);
  private sellerCodeStorage = inject(SellerCodeStorageService);
  private gtm = inject(GtmService);

  checkForDirectAddToCart(): void {
    this.route.queryParams.subscribe(params => {
      if (params['add-to-cart'] !== undefined) {
        const productUuid = params['product'];
        const variationUuid = params['variation'];
        const quantity = parseInt(params['quantity'] || '1', 10);
        const itemsParam = params['items'];
        const orderUuid = String(params['order'] ?? '')
          .trim();
        const shouldOpenCart =
          orderUuid.length > 0 || params['open-cart'] === 'true';
        const sellerCode = String(params['sellerCode'] ?? '')
          .trim()
          .toUpperCase();

        if (sellerCode) {
          this.sellerCodeStorage.set(sellerCode);
        }

        // Navega para home imediatamente para evitar reprocessamento,
        // mantendo sellerCode na URL quando presente.
        const navigateExtras = sellerCode
          ? { replaceUrl: true, queryParams: { sellerCode } }
          : { replaceUrl: true };
        this.router.navigate(['/'], navigateExtras);

        if (orderUuid) {
          this.addToCartFromOrderUuid(orderUuid, shouldOpenCart);
          return;
        }

        const items = this.tryParseItems(itemsParam);
        if (items && items.length > 0) {
          this.addItemsDirectlyToCart(items, shouldOpenCart);
          return;
        }

        if (!productUuid) {
          this.toastService.showError(
            this.i18nService.instant('errors.urlCart.missingUuid')
          );
          return;
        }
        this.addProductDirectlyToCart(productUuid, variationUuid, quantity, shouldOpenCart);
      } else if (params['sellerCode']) {
        this.sellerCodeStorage.set(String(params['sellerCode']));
      }
    });
  }

  private addToCartFromOrderUuid(
    orderUuid: string,
    shouldOpenCart: boolean
  ): void {
    this.orderService
      .getOrderDetailsByUuid(orderUuid)
      .pipe(
        timeout(10000),
        take(1),
        catchError(() => of(null as OrderDetailsResponse | null))
      )
      .subscribe((response) => {
        if (!response || response.status !== 'success' || !response.data) {
          this.toastService.showError(
            this.i18nService.instant('errors.urlCart.orderRecoverError')
          );
          return;
        }

        const data = response.data;
        if ((data?.paymentMethod ?? '') === 'MIND') {
          return;
        }

        const lines = retryLinesFromOrderData(data);
        if (lines.length === 0) {
          return;
        }

        this.addItemsDirectlyToCart(lines, shouldOpenCart, true);
      });
  }

  private tryParseItems(raw: unknown): { productUuid: string; parentProductUuid?: string | null; quantity?: number }[] | null {
    if (!raw || typeof raw !== 'string') {
      return null;
    }
    try {
      // `buildRetryCartQueryParams` applies encodeURIComponent; queryParams gives us the encoded string.
      const decoded = decodeURIComponent(raw);
      const parsed = JSON.parse(decoded);
      if (!Array.isArray(parsed)) {
        return null;
      }
      return parsed
        .filter((x) => x && typeof x.productUuid === 'string' && x.productUuid.trim() !== '')
        .map((x) => ({
          productUuid: x.productUuid,
          parentProductUuid: typeof x.parentProductUuid === 'string' ? x.parentProductUuid : (x.parentProductUuid ?? null),
          quantity: typeof x.quantity === 'number' ? x.quantity : parseInt(String(x.quantity ?? '1'), 10),
        }));
    } catch {
      return null;
    }
  }

  private addItemsDirectlyToCart(
    items: { productUuid: string; parentProductUuid?: string | null; quantity?: number }[],
    shouldOpenCart: boolean,
    orderRecovery: boolean = false
  ): void {
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }
    const normalized = items
      .map((it) => ({
        productUuid: it.productUuid,
        quantity: Math.max(1, Number.isFinite(it.quantity as number) ? (it.quantity as number) : 1),
        parentProductUuid: it.parentProductUuid ?? null,
      }))
      .filter((it) => !!it.productUuid);

    if (normalized.length === 0) {
      return;
    }

    if (shouldOpenCart) {
      this.cartService.toggleCartSidebar();
    }
    this.cartFacade.setUrlCartLoading(true);

    from(normalized)
      .pipe(
        concatMap((it) =>
          this.addOneRetryLine$(
            it.productUuid,
            it.parentProductUuid,
            it.quantity,
            orderRecovery
          ).pipe(catchError(() => of(false)))
        ),
        reduce((acc, ok) => acc + (ok ? 1 : 0), 0)
      )
      .subscribe({
        next: (successCount) => {
          setTimeout(() => {
            this.cartFacade.setUrlCartLoading(false);
            this.authService.markCartAsModified();
            if (orderRecovery) {
              if (successCount > 0) {
                this.toastService.showSuccess(
                  this.i18nService.instant('errors.urlCart.orderRecoverSuccess')
                );
              } else {
                this.toastService.showError(
                  this.i18nService.instant('errors.urlCart.orderRecoverError')
                );
              }
            }
          }, 200);
        },
        error: () => {
          this.cartFacade.setUrlCartLoading(false);
          if (orderRecovery) {
            this.toastService.showError(
              this.i18nService.instant('errors.urlCart.orderRecoverError')
            );
          }
        },
      });
  }

  private addOneRetryLine$(
    productUuid: string,
    parentProductUuid: string | null | undefined,
    quantity: number,
    silent: boolean = false
  ): Observable<boolean> {
    const parent = typeof parentProductUuid === 'string' ? parentProductUuid.trim() : '';
    const hasParent = !!parent;
    const mainProductUuid = hasParent ? parent : productUuid;
    const variationUuid = hasParent ? productUuid : undefined;

    return this.productV2Service.getProductByUuid(mainProductUuid).pipe(
      switchMap((response) => {
        if (response.error) {
          if (!silent) {
            this.toastService.showError(this.i18nService.instant('errors.urlCart.loadError'));
          }
          return of(false);
        }

        const product = response.data;
        if (product) {
          this.availableOffersFacade.mapProductVariationsToOffers(product);
        }

        let selectedVariation: ProductV2Variation | undefined;
        if (variationUuid && product?.variations) {
          selectedVariation = product.variations.find((v) => v.uuid === variationUuid);
          if (!selectedVariation) {
            if (!silent) {
              this.toastService.showError(
                this.i18nService.instant('errors.urlCart.variationNotFound', { uuid: variationUuid })
              );
            }
            return of(false);
          }
        }

        return this.waitForCartStability().pipe(
          switchMap(() =>
            combineLatest([
              this.cartFacade.hasItemInCart(product, selectedVariation),
              this.cartFacade.getItemQuantityInCart(product, selectedVariation),
            ])
          ),
          take(1),
          map(([exists, currentQuantity]) => {
            if (exists) {
              const newQuantity = currentQuantity + quantity;
              const itemId = this.cartFacade.getItemId(product, selectedVariation);
              this.cartFacade.updateCartItemQuantity(itemId, newQuantity);
            } else {
              this.cartFacade.addToCart(product, quantity, selectedVariation, {
                suppressSuccessToast: silent,
              });
            }
            return true;
          }),
          catchError((error) => {
            if (!silent) {
              console.error('❌ Erro ao processar carrinho:', error);
              this.toastService.showError(this.i18nService.instant('errors.urlCart.processError'));
            }
            return of(false);
          })
        );
      }),
      catchError(() => {
        if (!silent) {
          this.toastService.showError(
            this.i18nService.instant('errors.urlCart.productError', { uuid: mainProductUuid })
          );
        }
        return of(false);
      })
    );
  }

  private waitForCartStability() {
    return this.cartFacade.getCartItems().pipe(
      filter(items => items !== null),
      take(1),
      switchMap(() => {
        // Aguarda para garantir que sincronizações terminaram
        return timer(1500);
      })
    );
  }

  private addProductDirectlyToCart(
    productUuid: string,
    variationUuid?: string,
    quantity: number = 1,
    shouldOpenCart: boolean = false
  ): void {   
    if (shouldOpenCart) {
      this.cartService.toggleCartSidebar();
    }
    this.productV2Service.getProductByUuid(productUuid).subscribe({
      next: response => {
        if (response.error) {
          this.toastService.showError(
            this.i18nService.instant('errors.urlCart.loadError')
          );
          return;
        }

        const product = response.data;
        if (product) {
          this.availableOffersFacade.mapProductVariationsToOffers(product);
        }        
        let selectedVariation: ProductV2Variation | undefined;

        if (variationUuid && product.variations) {
          selectedVariation = product.variations.find(
            v => v.uuid === variationUuid
          );
          
          if (!selectedVariation) {
            console.error('❌ Variação não encontrada:', variationUuid);
            this.toastService.showError(
              this.i18nService.instant('errors.urlCart.variationNotFound', { uuid: variationUuid })
            );
            return;
          }
        }

        this.cartFacade.setUrlCartLoading(true);

        // Aguardar o carrinho estar estável antes de verificar
        this.waitForCartStability().pipe(
          switchMap(() => {
            return combineLatest([
              this.cartFacade.hasItemInCart(product, selectedVariation),
              this.cartFacade.getItemQuantityInCart(product, selectedVariation)
            ]);
          }),
          take(1)
        ).subscribe({
          next: ([exists, currentQuantity]) => {
            if (exists) {
              const newQuantity = currentQuantity + quantity;
              const itemId = this.cartFacade.getItemId(product, selectedVariation);
              this.cartFacade.updateCartItemQuantity(itemId, newQuantity);
            } else {
              this.cartFacade.addToCart(product, quantity, selectedVariation);
            }
            this.trackDirectAddToCart(product, quantity, selectedVariation);
             setTimeout(() => {
               this.cartFacade.setUrlCartLoading(false);
               this.authService.markCartAsModified();
             }, 200);
          },
          error: (error) => {
            console.error('❌ Erro ao processar carrinho:', error);
            this.cartFacade.setUrlCartLoading(false);
            this.toastService.showError(
              this.i18nService.instant('errors.urlCart.processError')
            );
          }
        });
      },
      error: _err => {
        this.cartFacade.setUrlCartLoading(false);
        this.toastService.showError(
          this.i18nService.instant('errors.urlCart.productError', { uuid: productUuid })
        );
      },
    });
  }

  private trackDirectAddToCart(
    product: ProductV2,
    quantity: number,
    selectedVariation?: ProductV2Variation
  ): void {
    const item: Ga4Item = {
      item_id: product.sku || product.uuid || product.id,
      item_name: product.name,
      item_category: product.categories?.[0]?.name,
      item_variant: selectedVariation?.name,
      price:
        selectedVariation?.salesPrice ??
        selectedVariation?.price ??
        product.salesPrice ??
        product.price ??
        0,
      quantity,
    };
    this.gtm.addToCart([item]);
  }
}
